import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { TermsModal } from '@/components/common/TermsModal';
import { FOOTER_NAV_COLUMNS } from '@/lib/constants/public-nav';

const CURRENT_YEAR = new Date().getFullYear();

const SUPPORT_EMAIL = 'info@mynextraise.com';

// Split into two sub-columns Figma's own footer uses, rather than one tall
// column - matches the reference layout's proportions. The page links come
// from the one shared list (FR-305, design §54 D7), so the footer cannot keep
// a label the header has renamed; that list carries the `/partners` vs
// `/trusted-partners` note (A-126) along with them.
//
// "Contact" has no dedicated public page - routed to the support mailto, the
// same "link to the real destination" treatment as every other item here, so
// it stays footer-local and is appended to the second column.
// "Events" removed (MYN-269 follow-up, explicit instruction) - it linked to
// the real in-app Events page, but that page is itself static (no dynamic
// listing behind it yet), so the footer link was removed rather than kept
// pointing at a non-functional destination.
const EXPLORE_COLUMNS = [
  FOOTER_NAV_COLUMNS[0],
  [...FOOTER_NAV_COLUMNS[1], { label: 'Contact', href: `mailto:${SUPPORT_EMAIL}` }],
] as const;

const CONTACT_LINKS = [
  { label: 'Email Us', href: `mailto:${SUPPORT_EMAIL}` },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/my-next-raise/' },
] as const;

export function Footer() {
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <>
    <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    <footer className="bg-navy text-white py-[50px] px-4">
      {/* max-w-328 (= 1312px) matches every section renderer and PublicHeader's
        own container exactly (MYN-328, FR-315, design §56 D4). This used to
        be `max-w-[1312px]` on this div with a fluid `clamp(16px, 12vw, 240px)`
        horizontal padding on the <footer> above it - at ordinary desktop
        widths that padding was far larger than every other component's fixed
        16px, which is the actual cause of the footer's left edge not lining
        up with the header/section content above it. */}
      <div className="max-w-328 mx-auto">
        {/* Top — brand + columns */}
        <div
          className="flex flex-col md:flex-row justify-between flex-wrap"
          style={{ gap: 48, paddingBottom: 32 }}
        >
          {/* Brand column */}
          <div className="max-w-xs">
            <a href="/" aria-label={`${UI_STRINGS.BRAND_NAME} home`}>
              <Logo variant="white" height={70} aria-hidden />
            </a>
          </div>

          {/* Explore - its own top-level column, not nested with Contact, so
            justify-between's leftover space splits into two normal gaps
            instead of one oversized one (matches the reference layout). */}
          <div className="flex flex-col gap-3">
            <h4
              className="text-white font-semibold uppercase m-0"
              style={{ fontSize: 18, letterSpacing: '0.70px' }}
            >
              Explore
            </h4>
            <div className="flex" style={{ gap: 64 }}>
              {EXPLORE_COLUMNS.map((column, i) => (
                <ul key={i} className="list-none m-0 p-0 flex flex-col gap-3">
                  {column.map((item) => (
                    <li key={item.label}>
                      {'to' in item ? (
                        <Link
                          to={item.to}
                          className="text-slate hover:text-white transition-colors"
                          style={{ fontSize: 16, fontWeight: 400 }}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <a
                          href={item.href}
                          className="text-slate hover:text-white transition-colors"
                          style={{ fontSize: 16, fontWeight: 400 }}
                        >
                          {item.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="flex flex-col gap-3">
            <h4
              className="text-white font-semibold uppercase m-0"
              style={{ fontSize: 18, letterSpacing: '0.70px' }}
            >
              Contact
            </h4>
            <ul className="list-none m-0 p-0 flex flex-col gap-3">
              {CONTACT_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    className="text-link-blue font-semibold hover:opacity-80 transition-opacity"
                    style={{ fontFamily: 'var(--font-sans)', fontSize: 16 }}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-none h-px bg-white/10 m-0" />

        {/* Bottom — copyright + T&C */}
        <div
          className="flex flex-col sm:flex-row justify-between items-center flex-wrap"
          style={{ gap: 16, paddingTop: 32 }}
        >
          <p
            className="text-white/40 m-0"
            style={{ fontSize: 16, fontWeight: 400 }}
          >
            © {CURRENT_YEAR} {UI_STRINGS.BRAND_NAME}. All rights reserved.
          </p>
          <button
            type="button"
            onClick={() => setTermsOpen(true)}
            className="text-white/40 hover:text-white/70 transition-colors"
            style={{ fontSize: 16, fontWeight: 400 }}
          >
            Terms &amp; Conditions
          </button>
        </div>
      </div>
    </footer>
    </>
  );
}
